import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as styles from './MenuBar.css.ts';
import logger from '../../utils/logger';
import { useProjectStore } from '../../stores/projectStore';
import { SaveFileModal } from '../SaveFileModal';
import { ExportCSVDialog } from '../ExportCSVDialog';
import { ImportDataModal } from '../ImportDataModal';
import { AboutDialog } from '../AboutDialog/AboutDialog';
import { ReleaseNotesDialog } from '../AboutDialog/ReleaseNotesDialog';
import { SettingsModal } from '../SettingsModal';
import { TabCreationModal } from '../TabCreationModal';
import { downloadCsvFile } from '../../utils/webFileDownload';
import { exportDataset } from '../../utils/dataExportUtils';
import { getTabCreationOptions } from '../../utils/tab-creation-utils';
import { createTabWithContext, getSelectableItems as getSelectableItemsHelper } from '../../tab-system/tab-creation-helper';
import { createNewReport } from '../../utils/reportUtils';
import type { DataSet } from '../../../types';

interface MenuItem {
  id: string;
  label?: string;
  shortcut?: string;
  divider?: boolean;
  action?: () => void;
  disabled?: boolean;
  submenu?: MenuItem[];
}

interface Menu {
  id: string;
  label: string;
  items: MenuItem[];
}

interface MenuBarProps {
  onSave?: () => void;
  onSaveAs?: () => void;
  onClose?: () => void;
  onNewTab?: (tabType: string) => void;
  onOpenFile?: () => void;
  tabCreationComboBox?: React.ReactNode;
  onReturnToLauncher?: () => void;
}

export const MenuBar: React.FC<MenuBarProps> = ({
  onSave,
  onSaveAs,
  onNewTab,
  tabCreationComboBox,
  onReturnToLauncher
}) => {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCSVExportDialog, setShowCSVExportDialog] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAboutDialog, setShowAboutDialog] = useState(false);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [showTabCreationModal, setShowTabCreationModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const menuBarRef = useRef<HTMLDivElement>(null);

  const projectStore = useProjectStore();
  const project = useProjectStore((state) => state.project);
  const workspaces = project?.workspaces ?? [];
  const activeWorkspaceId = project?.activeWorkspaceId;
  const createWorkspace = useProjectStore((state) => state.createWorkspace);
  const deleteWorkspace = useProjectStore((state) => state.deleteWorkspace);
  const switchWorkspace = useProjectStore((state) => state.switchWorkspace);
  const getCurrentWorkspace = useProjectStore((state) => state.getCurrentWorkspace);
  const getActiveContainer = useProjectStore((state) => state.getActiveContainer);
  const addTab = useProjectStore((state) => state.addTab);
  const splitPane = useProjectStore((state) => state.splitPane);
  const closePane = useProjectStore((state) => state.closePane);

  // CSVエクスポートハンドラー
  const handleExportCSV = useCallback((datasetIds: string[]) => {
    if (datasetIds.length === 0) {
      logger.warn('[MenuBar] No dataset selected for export');
      return;
    }

    const datasetId = datasetIds[0]; // 単一選択のみサポート
    const dataset = projectStore.getDataset(datasetId);

    if (!dataset) {
      logger.error('[MenuBar] Dataset not found:', datasetId);
      return;
    }

    try {
      logger.info('[MenuBar] Exporting dataset as CSV', {
        datasetId: dataset.id,
        datasetName: dataset.name,
        rowCount: dataset.rowCount,
        columnCount: Object.keys(dataset.schema.columns).length
      });

      // CSVコンテンツを生成
      const csvContent = exportDataset(dataset, {
        format: 'csv',
        includeHeaders: true
      });

      // ファイル名を生成（安全な文字のみ使用）
      const safeName = dataset.name
        .replace(/[<>:"|?*/\\]/g, '_')
        .replace(/\s+/g, '_')
        .toLowerCase();

      // CSVファイルをダウンロード
      downloadCsvFile(csvContent, safeName);

      logger.info('[MenuBar] CSV export completed successfully');
    } catch (error) {
      logger.error('[MenuBar] Failed to export CSV', error as Error);
    }
  }, [projectStore]);

  // タブ作成ハンドラー
  const handleTabCreation = useCallback((tabType: string, selectedItemId?: string) => {
    logger.info('[MenuBar] Creating tab', { tabType, selectedItemId });

    const datasets = projectStore.getAllDatasets().reduce((acc, ds) => {
      acc[ds.id] = ds;
      return acc;
    }, {} as Record<string, DataSet>);

    // 新規レポート作成の場合、Reportオブジェクトも作成
    let reports = projectStore.reports;
    if (tabType === 'report' && selectedItemId === '__new__' && projectStore.project) {
      const newReport = createNewReport('New Report');
      projectStore.updateProject((currentProject) => ({
        ...currentProject,
        reports: [...(currentProject.reports || []), newReport]
      }));
      reports = [...reports, newReport];
      selectedItemId = newReport.id;
      logger.info('[MenuBar] Created new report:', { reportId: newReport.id });
    }

    const context = {
      datasets,
      reports,
      activeDatasetId: undefined
    };

    const result = createTabWithContext(tabType, context, selectedItemId);

    if (result.error || !result.tab) {
      logger.error('[MenuBar] Failed to create tab', { error: result.error });
      return;
    }

    // アクティブなコンテナにタブを追加
    const activeContainer = getActiveContainer();
    if (activeContainer && result.tab) {
      addTab(activeContainer.id, result.tab);
      logger.info('[MenuBar] Tab created successfully', {
        tabId: result.tab.id,
        tabType: result.tab.type,
        containerId: activeContainer.id
      });
    } else {
      logger.error('[MenuBar] No active container found');
    }

    setShowTabCreationModal(false);
  }, [projectStore, getActiveContainer, addTab]);

  // メニュー定義
  const menus: Menu[] = [
    {
      id: 'file',
      label: 'File',
      items: [
        {
          id: 'project-overview',
          label: 'Project Overview',
          action: () => {
            logger.info('[MenuBar] Open project overview');
            onNewTab?.('project-overview');
            setActiveMenu(null);
          }
        },
        {
          id: 'project-lineage',
          label: 'Project Lineage',
          action: () => {
            logger.info('[MenuBar] Open project lineage');
            onNewTab?.('project-lineage');
            setActiveMenu(null);
          }
        },
        {
          id: 'compare-project',
          label: 'Compare with File...',
          action: () => {
            logger.info('[MenuBar] Compare project');
            onNewTab?.('project-diff');
            setActiveMenu(null);
          }
        },
        { id: 'divider1', divider: true },
        {
          id: 'save-browser',
          label: 'Save to Browser',
          shortcut: 'Cmd+S',
          action: () => {
            logger.info('[MenuBar] Save to browser storage');
            onSave?.();
            setActiveMenu(null);
          },
          disabled: !onSave
        },
        {
          id: 'export-project',
          label: 'Export Project...',
          shortcut: 'Cmd+Shift+S',
          action: () => {
            logger.info('[MenuBar] Export project');
            onSaveAs?.();
            setActiveMenu(null);
          },
          disabled: !onSaveAs
        },
        { id: 'divider3', divider: true },
        {
          id: 'export-csv',
          label: 'Export Data as CSV...',
          action: () => {
            logger.info('[MenuBar] Export CSV');
            setShowCSVExportDialog(true);
            setActiveMenu(null);
          }
        },
        { id: 'divider4', divider: true },
        {
          id: 'close',
          label: 'Close Project',
          action: () => {
            logger.info('[MenuBar] Close project - Return to launcher');
            if (onReturnToLauncher) {
              // TODO: 未保存の変更がある場合は警告を表示
              onReturnToLauncher();
            }
            setActiveMenu(null);
          },
          disabled: !onReturnToLauncher
        }
      ]
    },
    {
      id: 'view',
      label: 'View',
      items: [
        {
          id: 'new-tab',
          label: 'New Tab',
          shortcut: 'Cmd+T',
          action: () => {
            logger.info('[MenuBar] Open tab creation dialog');
            setShowTabCreationModal(true);
            setActiveMenu(null);
          }
        },
        { id: 'divider1', divider: true },
        {
          id: 'split-pane',
          label: 'Split Pane',
          submenu: [
            {
              id: 'split-horizontal',
              label: 'Split Horizontally',
              action: () => {
                logger.info('[MenuBar] Split horizontal');
                const activeContainer = getActiveContainer();
                if (activeContainer) {
                  splitPane(activeContainer.id, 'horizontal', 'after');
                }
                setActiveMenu(null);
              }
            },
            {
              id: 'split-vertical',
              label: 'Split Vertically',
              action: () => {
                logger.info('[MenuBar] Split vertical');
                const activeContainer = getActiveContainer();
                if (activeContainer) {
                  splitPane(activeContainer.id, 'vertical', 'after');
                }
                setActiveMenu(null);
              }
            }
          ]
        },
        {
          id: 'close-pane',
          label: 'Close Pane',
          action: () => {
            logger.info('[MenuBar] Close pane');
            const activeContainer = getActiveContainer();
            if (activeContainer) {
              // アクティブなペインをタブごと全て閉じる
              closePane(activeContainer.id);
              logger.info('[MenuBar] Closed pane with all tabs');
            } else {
              logger.warn('[MenuBar] No active pane to close');
            }
            setActiveMenu(null);
          }
        },
        { id: 'divider2', divider: true },
        {
          id: 'fullscreen',
          label: 'Enter Full Screen',
          shortcut: 'F11',
          action: () => {
            logger.info('[MenuBar] Toggle fullscreen');
            if (!document.fullscreenElement) {
              document.documentElement.requestFullscreen();
            } else {
              document.exitFullscreen();
            }
            setActiveMenu(null);
          }
        }
      ]
    },
    {
      id: 'data',
      label: 'Data',
      items: [
        {
          id: 'import-data',
          label: 'Import Data...',
          action: () => {
            logger.info('[MenuBar] Import data');
            setShowImportModal(true);
            setActiveMenu(null);
          }
        },
        {
          id: 'convert-column-types',
          label: 'Convert Column Types...',
          action: () => {
            logger.info('[MenuBar] Open column type conversion');
            onNewTab?.('column-type-conversion');
            setActiveMenu(null);
          }
        },
        {
          id: 'dummy-coding',
          label: 'Dummy Coding...',
          action: () => {
            logger.info('[MenuBar] Open dummy coding');
            onNewTab?.('dummy-coding');
            setActiveMenu(null);
          }
        },
        { id: 'divider1', divider: true },
        {
          id: 'sql-editor',
          label: 'SQL Query Editor',
          action: () => {
            logger.info('[MenuBar] SQL Editor');
            onNewTab?.('sql-editor');
            setActiveMenu(null);
          }
        }
      ]
    },
    {
      id: 'analysis',
      label: 'Analysis',
      items: [
        {
          id: 'descriptive',
          label: 'Descriptive Statistics',
          action: () => {
            logger.info('[MenuBar] Descriptive statistics');
            onNewTab?.('statistics');
            setActiveMenu(null);
          }
        },
        { id: 'divider1', divider: true },
        {
          id: 'glm',
          label: 'Generalized Linear Model (GLM)...',
          action: () => {
            logger.info('[MenuBar] GLM');
            onNewTab?.('glm');
            setActiveMenu(null);
          }
        },
        {
          id: 'crosstab',
          label: 'Crosstab Analysis...',
          action: () => {
            logger.info('[MenuBar] Crosstab');
            onNewTab?.('crosstab');
            setActiveMenu(null);
          }
        },
        {
          id: 'graph-builder',
          label: 'Graph Builder...',
          action: () => {
            logger.info('[MenuBar] Graph Builder');
            onNewTab?.('graph-builder');
            setActiveMenu(null);
          }
        }
      ]
    },
    {
      id: 'workspace',
      label: 'Workspace',
      items: [
        {
          id: 'new-workspace',
          label: 'New Workspace',
          shortcut: 'Cmd+Shift+N',
          action: () => {
            logger.info('[MenuBar] Creating new workspace');
            const newWorkspaceId = createWorkspace({
              name: `Workspace ${workspaces.length + 1}`
            });
            switchWorkspace(newWorkspaceId);
            logger.info('[MenuBar] Created and switched to new workspace:', newWorkspaceId);
            setActiveMenu(null);
          }
        },
        {
          id: 'close-workspace',
          label: 'Close Workspace',
          action: () => {
            logger.info('[MenuBar] Closing current workspace');
            const currentWorkspace = getCurrentWorkspace();
            if (currentWorkspace && workspaces.length > 1) {
              // 複数のワークスペースがある場合のみ削除可能
              deleteWorkspace(currentWorkspace.id);
              logger.info('[MenuBar] Closed workspace:', currentWorkspace.id);
            } else {
              logger.warn('[MenuBar] Cannot close the only workspace');
            }
            setActiveMenu(null);
          },
          // ワークスペースが1つしかない場合は無効化
          disabled: workspaces.length <= 1
        },
        { id: 'divider1', divider: true },
        {
          id: 'next-workspace',
          label: 'Next Workspace',
          shortcut: 'Cmd+}',
          action: () => {
            logger.info('[MenuBar] Next workspace');
            const currentIndex = workspaces.findIndex(
              w => w.id === activeWorkspaceId
            );
            if (currentIndex !== -1 && currentIndex < workspaces.length - 1) {
              const nextWorkspace = workspaces[currentIndex + 1];
              switchWorkspace(nextWorkspace.id);
              logger.info('[MenuBar] Switched to next workspace:', nextWorkspace.id);
            }
            setActiveMenu(null);
          },
          disabled: (() => {
            const currentIndex = workspaces.findIndex(
              w => w.id === activeWorkspaceId
            );
            return currentIndex === -1 || currentIndex >= workspaces.length - 1;
          })()
        },
        {
          id: 'prev-workspace',
          label: 'Previous Workspace',
          shortcut: 'Cmd+{',
          action: () => {
            logger.info('[MenuBar] Previous workspace');
            const currentIndex = workspaces.findIndex(
              w => w.id === activeWorkspaceId
            );
            if (currentIndex > 0) {
              const prevWorkspace = workspaces[currentIndex - 1];
              switchWorkspace(prevWorkspace.id);
              logger.info('[MenuBar] Switched to previous workspace:', prevWorkspace.id);
            }
            setActiveMenu(null);
          },
          disabled: (() => {
            const currentIndex = workspaces.findIndex(
              w => w.id === activeWorkspaceId
            );
            return currentIndex <= 0;
          })()
        },
        { id: 'divider2', divider: true },
        {
          id: 'minimize',
          label: 'Minimize',
          shortcut: 'Cmd+M',
          action: () => {
            logger.info('[MenuBar] Minimize');
            // Minimize実装
            setActiveMenu(null);
          }
        }
      ]
    },
    {
      id: 'help',
      label: 'Help',
      items: [
        {
          id: 'documentation',
          label: 'Documentation',
          submenu: [
            {
              id: 'documentation-ja',
              label: 'Japanese',
              action: () => {
                logger.info('[MenuBar] Documentation (Japanese)');
                window.open('/docs/ja/index.html', '_blank');
                setActiveMenu(null);
              }
            },
            {
              id: 'documentation-en',
              label: 'English',
              disabled: true,
              action: () => {
                logger.info('[MenuBar] Documentation (English)');
                window.open('/docs/en/index.html', '_blank');
                setActiveMenu(null);
              }
            }
          ]
        },
        {
          id: 'release-notes',
          label: 'Release Notes',
          action: () => {
            logger.info('[MenuBar] Release Notes');
            setShowReleaseNotes(true);
            setActiveMenu(null);
          }
        },
        { id: 'divider1', divider: true },
        {
          id: 'settings',
          label: 'Settings...',
          action: () => {
            logger.info('[MenuBar] Settings');
            setShowSettingsModal(true);
            setActiveMenu(null);
          }
        },
        { id: 'divider2', divider: true },
        {
          id: 'about',
          label: 'About MIDAS',
          action: () => {
            logger.info('[MenuBar] About');
            setShowAboutDialog(true);
            setActiveMenu(null);
          }
        }
      ]
    }
  ];

  // メニューの開閉処理
  const handleMenuClick = (menuId: string) => {
    setActiveMenu(activeMenu === menuId ? null : menuId);
  };

  // ホバー時にメニュー切り替え
  const handleMenuHover = (menuId: string) => {
    if (activeMenu) {
      setActiveMenu(menuId);
    }
  };

  // 外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuBarRef.current && !menuBarRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    };

    if (activeMenu) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [activeMenu]);

  // Escapeキーで閉じる
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveMenu(null);
      }
    };

    if (activeMenu) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [activeMenu]);

  // サブメニューのレンダリング
  const renderSubmenu = (items: MenuItem[]) => {
    return (
      <div className={styles.submenu}>
        {items.map((item) => {
          if (item.divider) {
            return <div key={item.id} className={styles.divider} />;
          }
          return (
            <button
              key={item.id}
              className={`${styles.menuItem} ${item.disabled ? styles.disabled : ''}`}
              onClick={item.action}
              disabled={item.disabled}
            >
              <span className={styles.label}>{item.label}</span>
              {item.shortcut && (
                <span className={styles.shortcut}>{item.shortcut}</span>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div
        ref={menuBarRef}
        className={styles.menuBar}
      >
        <div className={styles.menuItems}>
          {menus.map((menu) => (
            <div
              key={menu.id}
              className={styles.menuContainer}
              onMouseEnter={() => handleMenuHover(menu.id)}
              onMouseLeave={() => {}}
            >
              <button
                className={`${styles.menuButton} ${activeMenu === menu.id ? styles.active : ''}`}
                onClick={() => handleMenuClick(menu.id)}
                aria-expanded={activeMenu === menu.id}
              >
                {menu.label}
              </button>
              {activeMenu === menu.id && (
              <div className={styles.dropdown}>
                {menu.items.map((item) => {
                  if (item.divider) {
                    return <div key={item.id} className={styles.divider} />;
                  }

                  if (item.submenu) {
                    return (
                      <div key={item.id} className={styles.submenuContainer}>
                        <button
                          className={`${styles.menuItem} ${styles.hasSubmenu} ${item.disabled ? styles.disabled : ''}`}
                          disabled={item.disabled}
                        >
                          <span className={styles.label}>{item.label}</span>
                          <span className={styles.arrow}>▶</span>
                        </button>
                        {renderSubmenu(item.submenu)}
                      </div>
                    );
                  }

                  return (
                    <button
                      key={item.id}
                      className={`${styles.menuItem} ${item.disabled ? styles.disabled : ''}`}
                      onClick={item.action}
                      disabled={item.disabled}
                    >
                      <span className={styles.label}>{item.label}</span>
                      {item.shortcut && (
                        <span className={styles.shortcut}>{item.shortcut}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        </div>

        {/* TabCreationComboBox を右側に配置 */}
        {tabCreationComboBox && (
          <div className={styles.comboBoxContainer}>
            {tabCreationComboBox}
          </div>
        )}
      </div>

      {/* モーダル */}
      {showSaveModal && <SaveFileModal isOpen={showSaveModal} onClose={() => setShowSaveModal(false)} onSave={onSaveAs || (() => {})} title="Save Project" />}
      {showCSVExportDialog && (
        <ExportCSVDialog
          isOpen={showCSVExportDialog}
          onClose={() => setShowCSVExportDialog(false)}
          datasets={projectStore.getAllDatasets()}
          onExport={handleExportCSV}
        />
      )}
      <ImportDataModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
      />
      <ReleaseNotesDialog
        isOpen={showReleaseNotes}
        onClose={() => setShowReleaseNotes(false)}
      />
      <AboutDialog
        isOpen={showAboutDialog}
        onClose={() => setShowAboutDialog(false)}
      />
      <TabCreationModal
        isOpen={showTabCreationModal}
        onClose={() => setShowTabCreationModal(false)}
        options={getTabCreationOptions(projectStore.getAllDatasets().length > 0)}
        onSelectOption={handleTabCreation}
        getSelectableItems={(optionId) => {
          const context = {
            datasets: projectStore.getAllDatasets().reduce((acc, ds) => {
              acc[ds.id] = ds;
              return acc;
            }, {} as Record<string, DataSet>),
            reports: projectStore.reports,
            activeDatasetId: undefined
          };
          return getSelectableItemsHelper(optionId, context);
        }}
      />
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />
    </>
  );
};